import React, { useMemo } from 'react';
import { batch } from 'react-redux';
import {
  ICohortRow,
  IRowMeta,
  removeErrorAction,
  setCohortRowAnalysis,
  setCohortRowTimeWindowValues,
} from '../../../../../redux/slices/oldBuilder';
import {
  activeSchemaIdSelector,
  errorPrimitiveSelector,
} from '../../../../../redux/selectors/builder';
import {
  DateKindKey,
  ExpressionModeKey,
  FunnelModeKey,
  IAnalysisFilter,
  ISearchCohortEntityFieldsFragment,
  QueryType,
  SimpleCohortTypeKey,
} from '../../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import * as BuilderUI from '../../../BuilderUI';
import { BuilderReportMenu } from '../../../BuilderFields';

interface IProps {
  rowMeta: IRowMeta;
  cohortRow: ICohortRow;
  title: string;
  queryType: QueryType.Expression | QueryType.Funnel | QueryType.Retention;
}

const getAnalysisFilter = (
  queryType: QueryType,
): IAnalysisFilter | undefined => {
  if (queryType === QueryType.Expression) {
    return {
      expressionFilter: {
        modes: [ExpressionModeKey.Query],
        dateKinds: [DateKindKey.Event],
        hasRollingControl: null,
      },
      funnelFilter: null,
    };
  }

  if (queryType === QueryType.Funnel) {
    return {
      expressionFilter: null,
      funnelFilter: {
        dateKinds: [DateKindKey.Event],
        modes: [
          FunnelModeKey.Frequency,
          FunnelModeKey.TimeToConvert,
          FunnelModeKey.Conversion,
        ],
      },
    };
  }
};

export default ({ rowMeta, cohortRow, title, queryType }: IProps) => {
  const dispatch = useAppDispatch();

  const { analysisId, simpleCohortTypeKey } = cohortRow;

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const errorSelector = useMemo(errorPrimitiveSelector, []);
  const errors = useAppSelector((state) =>
    errorSelector(state, rowMeta, 'analysisId'),
  );

  const handleChange = (
    newOption: ISearchCohortEntityFieldsFragment | null,
  ) => {
    batch(() => {
      if (newOption) {
        dispatch(removeErrorAction({ rowMeta, errorKey: 'analysisId' }));
      }
      // Retention Cohort is a special case, it has two diff type of time windows.
      // 1. regular retention cohort: daily, weekly, etc : using number as days or weeks on time window values
      // 2. custom retention cohort: 0 - 3 days, 3 - 7 days, etc : using number as index of time brackets
      // to reduce the confusion, after user select a new retention analysis, we should reset the time window values
      if (simpleCohortTypeKey === SimpleCohortTypeKey.Retention) {
        dispatch(
          setCohortRowTimeWindowValues({
            newValue: null,
            rowMeta: { ...rowMeta, numberIndex: 0 },
          }),
        );
        dispatch(
          setCohortRowTimeWindowValues({
            newValue: null,
            rowMeta: { ...rowMeta, numberIndex: 1 },
          }),
        );
      }
      dispatch(
        setCohortRowAnalysis({
          analysis: newOption,
          rowMeta,
          simpleCohortTypeKey,
        }),
      );
    });
  };

  const analysisFilter = getAnalysisFilter(queryType);
  return (
    <BuilderUI.BuilderFieldContainer>
      <BuilderUI.BuilderFieldLabel>{title}</BuilderUI.BuilderFieldLabel>
      <BuilderReportMenu
        activeSchemaId={activeSchemaId}
        analysisId={analysisId || null}
        queryType={queryType}
        onChange={handleChange}
        hasError={Boolean(errors)}
        analysisFilter={analysisFilter}
      />
    </BuilderUI.BuilderFieldContainer>
  );
};
